import PropTypes from 'prop-types';
import React from 'react';
import {
  useNavigate,
  useLocation,
  useParams,
  useSearchParams,
} from 'react-router-dom-v5-compat';
import { aOrAn } from '../utils/helpers';
import {
  claimTabHref,
  claimTabPath,
  isOnFilesView,
} from '../utils/claim-tab-helpers';
import { openMailFaxDetails, setPageFocus } from '../utils/page';
import {
  getIsSensitive,
  REQUEST_FOR_EVIDENCE_LABEL,
} from '../utils/trackedItemContent';

/**
 * Type1UnknownUploadError component displays an alert for unknown Type 1 upload errors
 * These are errors that occur before reaching Lighthouse where we can't identify
 * the specific cause (as opposed to known errors like duplicates or invalid claimant)
 * @param {Array} errorFiles - Array of error objects with fileName and docType
 * @param {Object} [trackedItem] - When set, the intro paragraph names the tracked-item
 *   request the upload was tied to. RequestNotifications passes this on tracked-item
 *   document-request pages; the layout-banner caller in ClaimDetailLayout leaves it
 *   undefined.
 */
export default function Type1UnknownUploadError({
  errorFiles,
  trackedItem,
  isChampvaFlow,
  useVaTabs = false,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: claimId } = useParams();
  const [searchParams] = useSearchParams();
  const typeParam = searchParams.get('type');
  const typeQuery = typeParam ? `?type=${typeParam}` : '';

  const handleLinkClick = e => {
    e.preventDefault();

    const isOnFilesPage = isOnFilesView(location);

    if (isChampvaFlow) {
      if (isOnFilesPage) {
        openMailFaxDetails();
      } else {
        navigate(
          useVaTabs
            ? claimTabPath(claimId, 'files', { search: typeQuery })
            : `../files${typeQuery}`,
          {
            state: { focusMailFaxDetails: true },
          },
        );
      }
    } else {
      if (!isOnFilesPage)
        navigate(
          useVaTabs
            ? claimTabPath(claimId, 'files', { hash: '#other-ways-to-send' })
            : '../files#other-ways-to-send',
        );
      setPageFocus('#other-ways-to-send');
    }
  };

  if (!errorFiles || errorFiles.length === 0) {
    return null;
  }

  const isSingle = errorFiles.length === 1;
  const file = isSingle
    ? { noun: 'file', demonstrative: 'this file' }
    : { noun: 'files', demonstrative: 'these files' };

  const showTrackedItemClause = !!trackedItem;
  const isSensitive = showTrackedItemClause && getIsSensitive(trackedItem);
  const requestLabel = trackedItem?.friendlyName || REQUEST_FOR_EVIDENCE_LABEL;
  const requestSuffix =
    requestLabel === REQUEST_FOR_EVIDENCE_LABEL ? '' : ' request';

  const trackedItemClause = showTrackedItemClause ? (
    <>
      {' '}
      for {aOrAn(requestLabel)}{' '}
      <strong data-dd-privacy={isSensitive ? 'mask' : undefined}>
        {requestLabel}
      </strong>
      {requestSuffix}
    </>
  ) : null;

  return (
    <>
      <p className="vads-u-margin-top--0">
        We're sorry. There was a problem with our system. We couldn't process
        the {file.noun} you tried to submit{trackedItemClause}:
      </p>
      {/* add explicit role=list to expose the <ul> as a list for Safari/VoiceOver */}
      {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
      <ul
        className="usa-unstyled-list"
        role="list"
        aria-label={
          isSingle
            ? "File that couldn't be processed"
            : "Files that couldn't be processed"
        }
      >
        {errorFiles.map(errorFile => (
          <li key={errorFile.fileName}>
            <p className="vads-u-margin-bottom--0">
              <strong
                data-dd-privacy="mask"
                data-dd-action-name="failed upload filename"
              >
                {errorFile.fileName}
              </strong>
            </p>
            <p className="vads-u-margin-top--0">
              {errorFile.docType || 'Unknown'}
            </p>
          </li>
        ))}
      </ul>
      <p>
        You can submit {file.demonstrative} by mail or{' '}
        {isChampvaFlow ? 'fax' : 'in person'} instead.
      </p>
      <p>
        If you try to submit {file.demonstrative} again in this tool, we expect
        we'll have the same problem.
      </p>
      <va-link-action
        href={
          useVaTabs
            ? claimTabHref(claimId, 'files', { hash: '#other-ways-to-send' })
            : '../files#other-ways-to-send'
        }
        text={
          isChampvaFlow
            ? 'Learn how to submit files by mail or fax'
            : 'Learn how to submit documents by mail or in person'
        }
        type="primary"
        onClick={handleLinkClick}
      />
    </>
  );
}

Type1UnknownUploadError.propTypes = {
  errorFiles: PropTypes.arrayOf(
    PropTypes.shape({
      fileName: PropTypes.string.isRequired,
      docType: PropTypes.string,
    }),
  ),
  isChampvaFlow: PropTypes.bool,
  trackedItem: PropTypes.shape({
    friendlyName: PropTypes.string,
    isSensitive: PropTypes.bool,
  }),
  useVaTabs: PropTypes.bool,
};
